"use client";

import { useLocale, useTranslations } from "next-intl";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePlayerStore } from "@/stores/player-store";

export const PLAYBACK_SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2] as const;

/** Playback speed picker for the mini player (persisted via the player store). */
export default function PlaybackSpeedMenu({
  buttonStyle,
}: {
  buttonStyle?: React.CSSProperties;
}) {
  const t = useTranslations("player");
  const locale = useLocale();
  const speed = usePlayerStore((s) => s.speed);
  const setSpeed = usePlayerStore((s) => s.setSpeed);

  // Locale-aware digits ("۱٫۵" in fa) — the "×" sign is language neutral.
  const fmt = (n: number) =>
    `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(n)}×`;

  return (
    <DropdownMenu dir="ltr">
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("speed")}
          title={t("speed")}
          style={{
            ...buttonStyle,
            width: "auto",
            minWidth: 44,
            padding: "0 8px",
            fontSize: 13,
            fontWeight: 650,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmt(speed)}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="end"
        className="z-[80] w-auto min-w-28"
      >
        <DropdownMenuRadioGroup
          value={String(speed)}
          onValueChange={(v) => setSpeed(Number(v))}
        >
          {PLAYBACK_SPEEDS.map((r) => (
            <DropdownMenuRadioItem key={r} value={String(r)}>
              {fmt(r)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
